/**
 * IpamSubnetTypesManager — alta y edición del catálogo de tipos de subred.
 *
 * El catálogo es global (lo comparten todas las empresas): `ipam_subnets.subnet_type`
 * guarda el `value`, así que dos empresas que compartan red ven la misma etiqueta.
 * Por eso `value` no se puede cambiar una vez creado; lo que se edita es la
 * etiqueta y el color, y se pueden añadir tipos nuevos. Un tipo en uso no se
 * puede borrar (el backend responde 409 con el número de subredes).
 */
import React, { useState } from 'react';
import api from '../services/api';
import { SUBNET_TYPE_COLOR_OPTIONS, subnetTypeLabel } from '../utils/ipamTypes';

const EMPTY = { value: '', label: '', color: 'secondary' };

export default function IpamSubnetTypesManager({ companyId, types = [], onChanged, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // tipo en edición (id)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const resetForm = () => { setForm(EMPTY); setEditing(null); };

  const startEdit = (type) => {
    setEditing(type.id);
    setForm({ value: type.value, label: type.label, color: type.color });
    setError('');
    setInfo('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');
    if (!form.label.trim()) { setError('La etiqueta es obligatoria.'); return; }
    if (!editing && !form.value.trim()) { setError('El identificador es obligatorio.'); return; }

    setSaving(true);
    try {
      if (editing) {
        await api.updateIpamSubnetType(editing, { label: form.label.trim(), color: form.color }, companyId);
        setInfo('Tipo actualizado.');
      } else {
        await api.createIpamSubnetType(
          { value: form.value.trim(), label: form.label.trim(), color: form.color },
          companyId
        );
        setInfo('Tipo añadido.');
      }
      resetForm();
      await onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo guardar el tipo');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type) => {
    if (!window.confirm(`¿Borrar el tipo «${type.label}»?`)) return;
    setError('');
    setInfo('');
    try {
      await api.deleteIpamSubnetType(type.id, companyId);
      setInfo('Tipo borrado.');
      if (editing === type.id) resetForm();
      await onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el tipo');
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg">
          <div className="modal-header py-3 border-bottom">
            <div>
              <h6 className="modal-title fw-bold mb-0">Tipos de subred</h6>
              <small className="text-muted">
                Catálogo compartido por todas las empresas · {types.length} tipos
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body px-4 py-3">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}
            {info && <div className="alert alert-success py-2 small">{info}</div>}

            <div className="table-responsive mb-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                  <tr style={{ fontSize: '0.8em' }}>
                    <th>Tipo</th>
                    <th>Identificador</th>
                    <th>Subredes</th>
                    <th style={{ width: 110 }}></th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.85em' }}>
                  {types.map((type) => (
                    <tr key={type.id || type.value} className={editing === type.id ? 'table-active' : ''}>
                      <td>
                        <span className={`badge text-bg-${type.color || 'secondary'}`} style={{ fontSize: '0.72em' }}>
                          {type.label}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{type.value}</td>
                      <td className="text-muted">{type.subnets_count ?? 0}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm py-0 px-1 me-1"
                          title="Editar etiqueta y color"
                          onClick={() => startEdit(type)}
                        >✏️</button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm py-0 px-1"
                          title="Borrar tipo"
                          onClick={() => remove(type)}
                        >🗑</button>
                      </td>
                    </tr>
                  ))}
                  {types.length === 0 && (
                    <tr><td colSpan={4} className="text-muted text-center py-3">Sin tipos.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <form onSubmit={submit} className="row g-2 align-items-end border-top pt-3">
              <div className="col-12 col-md-3">
                <label className="form-label small mb-1">Identificador</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. cctv"
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                  disabled={Boolean(editing) || saving}
                  title={editing ? 'El identificador no se puede cambiar' : ''}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label small mb-1">Etiqueta</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. CÁMARAS"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small mb-1">Color</label>
                <select
                  className="form-select form-select-sm"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  disabled={saving}
                >
                  {SUBNET_TYPE_COLOR_OPTIONS.map((color) => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-3 d-flex gap-1">
                <button className="btn btn-primary btn-sm flex-grow-1" type="submit" disabled={saving}>
                  {saving ? '…' : editing ? 'Guardar' : 'Añadir tipo'}
                </button>
                {editing && (
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm} disabled={saving}>
                    Cancelar
                  </button>
                )}
              </div>
              <div className="col-12">
                <small className="text-muted">
                  Vista previa:{' '}
                  <span className={`badge text-bg-${form.color}`} style={{ fontSize: '0.72em' }}>
                    {form.label.trim() || subnetTypeLabel(form.value)}
                  </span>
                </small>
              </div>
            </form>
          </div>

          <div className="modal-footer py-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
